import type { CSSProperties } from 'react'
import { Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { ContentType } from 'recharts/types/component/DefaultLegendContent'
import type { BizPieChartData } from '../BizTypes'
import { cn } from '@jotta/utils/css'
import { formatBytes } from '@jotta/utils/formatStorage'

type PieLabelProps = {
  index: number
  name: string
  value: number
  innerRadius: number
  outerRadius: number
  midAngle: number
  percent: number
  cx: number
  cy: number
  x: number
  y: number
  tooltipPosition: {
    x: number
    y: number
  }
}

const RADIAN = Math.PI / 180
const legendStyle: CSSProperties = {
  top: '50%',
  right: 0,
  transform: 'translate(0, -50%)',
  lineHeight: '24px',
  verticalAlign: 'middle',
  background: 'rgb(255 255 255 / 0.8)',
  maxWidth: '50%',
}
const labelFormatter = (value: any) => {
  return typeof value === 'number' ? formatBytes(value) : value
}
const renderLegend: ContentType = ({ payload = [] }) => {
  return (
    <ul className="body-sm-sh grid gap-3">
      {payload.map(({ color, value, payload }, index) => (
        <li key={`item-${index}`} className="flex items-center gap-2">
          <div
            className="size-4 shrink-0 rounded-full"
            style={{
              backgroundColor: color,
            }}
          />
          <span>{value}</span>
          <span>{payload && formatBytes(payload.value)}</span>
        </li>
      ))}
    </ul>
  )
}
export function BizPieChart({ data }: { data: BizPieChartData }) {
  return (
    <ResponsiveContainer>
      <PieChart className="aspect-[2/1]">
        <Pie
          nameKey="name"
          data={data}
          cx="50%"
          cy="50%"
          startAngle={90}
          endAngle={-360}
          outerRadius="90%"
          label={renderLabel}
          labelLine={false}
          dataKey="value"
        />
        <Legend
          content={renderLegend}
          layout="vertical"
          verticalAlign="middle"
          align="right"
          wrapperStyle={legendStyle}
        />
        <Tooltip formatter={labelFormatter} />
      </PieChart>
    </ResponsiveContainer>
  )
}
function renderLabel(props: PieLabelProps) {
  const { cx, cy, x, innerRadius, outerRadius, midAngle, percent } = props
  // debug('label', props)
  const radius = innerRadius + (outerRadius - innerRadius) * 0.7
  const innerX = cx + radius * Math.cos(-midAngle * RADIAN)
  const innerY = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <>
      <text
        x={innerX}
        y={innerY}
        fill={cn('text-primary')}
        textAnchor={x > cx ? 'middle' : 'middle'}
        dominantBaseline="middle"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    </>
  )
}
